import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from "react";
import { Stack, Typography } from "@mui/material";
import { useLocation, useNavigate } from "react-router-dom";
import { usePagesTranslation } from "../../../../pages/locales";
import {
    beginQuickPlay,
    fetchNewestSlidingGroup,
    fetchLastGame,
    pickTemplateFromGroup,
    playNavigateState,
    resumeCaption,
    resumeStoryTitle,
} from "../lib/api";
import ChooseGridPanel from "../components/ChooseGridPanel";
import PageShell from "../components/PageShell";
import PaywallSheet from "../components/PaywallSheet";
import ResumeLastGameCard from "../components/ResumeLastGameCard";
import {
    getSlidingStoryTitle,
    pickSlidingOriginalImageUrl,
} from "../../../../pages/browse-sliding/slidingBrowseUtils";
import {
    buildLocalPlayNavigateState,
    isSnapshotUiTesting,
    notifyNativeSnapshotMainReady,
    SNAPSHOT_MAIN_READY_LABEL,
} from "../lib/access";
import AuthService from "../../../../services/AuthService";
import { useSubscription } from "../lib/SubscriptionContext";
import {
    saveAuthenticatedPlayGridPreferences,
    subscribeAuthenticatedPlayGridPreferences,
} from "../lib/playGridPreferences";
import { captionSx } from "../styles/mainStyles";
import { attachPlayReturnPath, navigateToPlay } from "../lib/playRouteState";
import {
    chargePremiumPlayCreditOnEntry,
    isInsufficientCreditError,
    resolvePremiumPlayCreditContext,
} from "../lib/slidingCreditConsume";
import Logger from "../../../../services/Logger";
import { CONTENT_RECORDING_SLIDING_ID } from "../lib/contentRecording";
import { MAIN_PATH, PLAY_PATH, QUICK_PLAY_GRID, SETTINGS_PATH } from "../routes";

export default function MainPage() {
    const navigate = useNavigate();
    const location = useLocation();
    const { t } = usePagesTranslation();
    const { access, isSubscribed, isConfigurationLocked, credits } = useSubscription();
    const snapshotMode = isSnapshotUiTesting();
    const [lastGame, setLastGame] = useState(null);
    const [lastGameLoading, setLastGameLoading] = useState(true);
    const [gridSize, setGridSize] = useState(QUICK_PLAY_GRID);
    const [difficulty, setDifficulty] = useState("easy");
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState(null);
    const [newestGroup, setNewestGroup] = useState(null);
    const [newestLoading, setNewestLoading] = useState(true);
    const [showPaywallSheet, setShowPaywallSheet] = useState(false);
    /** `?recording=1080p` — capture entry point: open the content board (auto-solves). */
    const recordingMode =
        new URLSearchParams(location.search).get("recording") === "1080p";
    const recordingLaunchedRef = useRef(false);

    const loadLast = useCallback(async () => {
        setLastGameLoading(true);
        try {
            const session = await fetchLastGame();
            setLastGame(session);
        } finally {
            setLastGameLoading(false);
        }
    }, []);

    useEffect(() => {
        if (snapshotMode) return undefined;
        return subscribeAuthenticatedPlayGridPreferences((prefs) => {
            if (!prefs) return;
            if (isConfigurationLocked(prefs.gridSize, prefs.difficulty)) return;
            setGridSize(prefs.gridSize);
            setDifficulty(prefs.difficulty);
        });
    }, [snapshotMode, isConfigurationLocked]);

    useEffect(() => {
        if (snapshotMode) {
            setLastGameLoading(false);
            return;
        }
        if (access.loading) return;
        loadLast();
    }, [loadLast, location.key, snapshotMode, access.loading, access.isAuthenticated]);

    useEffect(() => {
        if (snapshotMode || access.loading) return undefined;
        let cancelled = false;
        setNewestLoading(true);
        fetchNewestSlidingGroup({ excludeCompleted: true })
            .then((group) => {
                if (!cancelled) setNewestGroup(group);
            })
            .catch(() => {
                if (!cancelled) setNewestGroup(null);
            })
            .finally(() => {
                if (!cancelled) setNewestLoading(false);
            });
        return () => {
            cancelled = true;
        };
    }, [location.key, access.loading, access.isAuthenticated, snapshotMode]);

    const goPlay = useCallback(
        (state) => {
            if (state) {
                navigateToPlay(
                    navigate,
                    PLAY_PATH,
                    attachPlayReturnPath(state, MAIN_PATH),
                );
            }
        },
        [navigate],
    );

    const persistPlayPrefs = useCallback((gs, diff) => {
        void saveAuthenticatedPlayGridPreferences({ gridSize: gs, difficulty: diff });
    }, []);

    const handleGridChange = useCallback(
        (size) => {
            setGridSize(size);
            persistPlayPrefs(size, difficulty);
        },
        [difficulty, persistPlayPrefs],
    );

    const handleDifficultyChange = useCallback(
        (d) => {
            setDifficulty(d);
            persistPlayPrefs(gridSize, d);
        },
        [gridSize, persistPlayPrefs],
    );

    const launchGame = async () => {
        setBusy(true);
        try {
            persistPlayPrefs(gridSize, difficulty);
            if (!(await AuthService.isAuthenticated())) {
                const picked = pickTemplateFromGroup(newestGroup, gridSize, difficulty);
                goPlay(
                    buildLocalPlayNavigateState({
                        gridSize,
                        difficulty,
                        story: newestGroup?.story,
                        storyTitle: newestGroup
                            ? getSlidingStoryTitle(newestGroup.story)
                            : undefined,
                        imageUrl: picked?.imageUrl,
                        previewImageUrl: newestGroup
                            ? pickSlidingOriginalImageUrl(newestGroup.story)
                            : undefined,
                    })
                );
                return;
            }
            const result = await beginQuickPlay(gridSize, difficulty);
            if (result.error) {
                setError(result.error);
                return;
            }
            const playState = result.state;
            if (playState?.slidingId) {
                try {
                    const creditCtx = await resolvePremiumPlayCreditContext({ access, credits });
                    await chargePremiumPlayCreditOnEntry({
                        slidingId: playState.slidingId,
                        gridSize,
                        difficulty,
                        hasLegacyUnlimitedAccess: creditCtx.hasLegacyUnlimitedAccess,
                        hasCredits: creditCtx.hasCredits,
                        applyCreditBalance: credits.applyCreditBalance,
                    });
                } catch (e) {
                    if (isInsufficientCreditError(e)) {
                        navigate(SETTINGS_PATH);
                        return;
                    }
                    Logger.warn("MainPage.launchGame credit charge", e);
                }
            }
            goPlay(playState);
        } finally {
            setBusy(false);
        }
    };

    // Recording capture: open the dedicated content board (auto-solves on the play page).
    useEffect(() => {
        if (!recordingMode || snapshotMode) return;
        if (recordingLaunchedRef.current) return;
        recordingLaunchedRef.current = true;
        navigate(
            `${PLAY_PATH}?slidingId=${encodeURIComponent(CONTENT_RECORDING_SLIDING_ID)}&recording=1080p`,
            { replace: true },
        );
    }, [recordingMode, snapshotMode, navigate]);

    /** Swift `handlePlayButtonTap()` — paywall when locked, else navigate to play. */
    const handlePlayButtonTap = () => {
        setError(null);
        if (isConfigurationLocked(gridSize, difficulty)) {
            setShowPaywallSheet(true);
            return;
        }
        void launchGame();
    };

    const resumeGame = () => {
        if (!lastGame) return;
        if (isConfigurationLocked(lastGame.gridSize, lastGame.difficulty)) {
            setShowPaywallSheet(true);
            return;
        }
        void (async () => {
            if (!(await AuthService.isAuthenticated())) {
                const state = buildLocalPlayNavigateState({
                    gridSize: lastGame.gridSize,
                    difficulty: lastGame.difficulty,
                    story: lastGame.story,
                    storyTitle: lastGame.storyTitle,
                    imageUrl: lastGame.imageUrl,
                    previewImageUrl: lastGame.previewImageUrl,
                    layout: lastGame.layout,
                    resume: true,
                });
                goPlay(state);
                return;
            }
            const state = playNavigateState(lastGame, { resume: true });
            if (state) goPlay(state);
        })();
    };

    const resumeGridLine = lastGame ? resumeCaption(lastGame) : null;
    const resumeTitle = lastGame ? resumeStoryTitle(lastGame) : null;
    const canResumeLast =
        Boolean(lastGame) && !isConfigurationLocked(lastGame.gridSize, lastGame.difficulty);

    const resumeStatus = lastGameLoading
        ? t("native.home.checkingSavedGame", { defaultValue: "Checking saved game…" })
        : lastGame
          ? resumeGridLine
          : t("native.home.noGameInProgress", {
                defaultValue: "No game in progress. Sign in to save.",
            });

    const playingTitle = newestGroup ? getSlidingStoryTitle(newestGroup.story) || null : null;

    useLayoutEffect(() => {
        if (!snapshotMode) return undefined;
        notifyNativeSnapshotMainReady();
        const timer = window.setTimeout(() => notifyNativeSnapshotMainReady(), 500);
        return () => window.clearTimeout(timer);
    }, [snapshotMode]);

    useEffect(() => {
        if (!snapshotMode || access.loading) return undefined;
        notifyNativeSnapshotMainReady();
        const timer = window.setTimeout(() => notifyNativeSnapshotMainReady(), 500);
        return () => window.clearTimeout(timer);
    }, [snapshotMode, access.loading]);

    return (
        <PageShell
            compact
            pageHero="home"
            heroChildren={
                <ResumeLastGameCard
                    onResume={resumeGame}
                    disabled={!canResumeLast || lastGameLoading || busy}
                    loading={lastGameLoading}
                    storyTitle={resumeTitle}
                    statusText={resumeStatus}
                    resumeHint={null}
                />
            }
        >
            {snapshotMode ? (
                <span
                    aria-label={SNAPSHOT_MAIN_READY_LABEL}
                    style={{
                        position: "absolute",
                        width: 1,
                        height: 1,
                        overflow: "hidden",
                        clip: "rect(0,0,0,0)",
                    }}
                >
                    {SNAPSHOT_MAIN_READY_LABEL}
                </span>
            ) : null}
            <Stack
                spacing={0}
                sx={{
                    flex: 1,
                    minHeight: 0,
                    display: "flex",
                    flexDirection: "column",
                }}
            >
                <ChooseGridPanel
                    storyTitle={playingTitle}
                    storyGroup={newestGroup}
                    loading={newestLoading}
                    gridSize={gridSize}
                    difficulty={difficulty}
                    onGridChange={handleGridChange}
                    onDifficultyChange={handleDifficultyChange}
                    onStart={handlePlayButtonTap}
                    starting={busy}
                    disabled={lastGameLoading || access.loading}
                />

                {error ? (
                    <Typography sx={{ ...captionSx, color: "#f87171", textAlign: "center", pt: 1 }}>
                        {error}
                    </Typography>
                ) : null}
            </Stack>

            <PaywallSheet
                open={showPaywallSheet}
                onClose={() => setShowPaywallSheet(false)}
                onUnlocked={() => void launchGame()}
            />
        </PageShell>
    );
}
